import { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';

import { httpClient } from '../dataProviders/httpClient';
import { PRENEED_DEFAULT, Region } from './pricing.constants';

type PlanType = 'single' | 'one_year' | 'three_year';

type PlanResult =
  | {
      planType: PlanType;
      available: true;
      factor: number;
      perPeriodCents: number;
      totalCents: number;
      periods: number;
    }
  | { planType: PlanType; available: false; reason: string };

/**
 * Row order, fixed. The table renders these three rows whether or not there is
 * a result to put in them — clearing the rows while someone retypes an age made
 * the section collapse and spring back on every keystroke.
 */
const PLAN_ORDER: PlanType[] = ['single', 'one_year', 'three_year'];

const PLAN_LABEL: Record<PlanType, string> = {
  single: 'Single payment',
  one_year: '12 months',
  three_year: '36 months',
};

const money = (cents: number) =>
  (cents / 100).toLocaleString('en-CA', {
    style: 'currency',
    currency: 'CAD',
    minimumFractionDigits: 2,
  });

/**
 * Preneed monthly quotes.
 *
 * The arithmetic is `TrustagePremiumService`, which has been region-aware for a
 * while — Ontario on the encoded 0088-911 rate card, Quebec on the legacy band
 * tables. All this adds is somewhere for staff to see it.
 *
 * All three plans are shown together because the comparison is the question a
 * family is actually asking.
 */
export const PreneedSection = ({ region }: { region: Region }) => {
  const [age, setAge] = useState('70');
  const [amount, setAmount] = useState(String(PRENEED_DEFAULT[region]));
  const [plans, setPlans] = useState<PlanResult[] | null>(null);
  /**
   * The preplanned amount the server quoted against — not the input field.
   * Interest is a difference, so measuring it against a field the user is
   * still editing would show a wrong number for a moment on every keystroke.
   */
  const [quotedCents, setQuotedCents] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  // Switching region is a deliberate act and the two regions start from
  // different amounts ($2,100 Quebec, $2,500 Ontario), so the field follows the
  // toggle rather than keeping a figure that belongs to the other province.
  useEffect(() => {
    setAmount(String(PRENEED_DEFAULT[region]));
  }, [region]);

  useEffect(() => {
    const ageNum = Number(age);
    const amountNum = Number(amount);
    if (!Number.isFinite(ageNum) || ageNum <= 0 || !Number.isFinite(amountNum) || amountNum <= 0) {
      setPlans(null);
      setQuotedCents(null);
      return;
    }

    // Debounced: these are two free-text fields, and a request per keystroke
    // would quote against half-typed ages.
    const timer = setTimeout(() => {
      setLoading(true);
      setError(null);
      const url =
        `${process.env.REACT_APP_PAPERMAN_API_URL}trustage/quote` +
        `?region=${region}&age=${ageNum}&preplannedCents=${Math.round(amountNum * 100)}`;

      void httpClient(url)
        .then(({ json }: any) => {
          setPlans(json.plans);
          setQuotedCents(json.preplannedCents);
          setLoading(false);
        })
        .catch((e: any) => {
          setError(e?.message ?? 'Could not reach the premium calculator');
          setPlans(null);
          setQuotedCents(null);
          setLoading(false);
        });
    }, 350);

    return () => clearTimeout(timer);
  }, [region, age, amount]);

  return (
    <Box sx={{ width: '100%' }}>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          TruStage premiums. Ontario and Quebec price differently at the same age, so
          the region toggle above changes these numbers.
        </Typography>

        <Stack direction="row" spacing={2} sx={{ mb: 2 }}>
          <TextField
            label="Age"
            size="small"
            value={age}
            onChange={(e) => setAge(e.target.value)}
            sx={{ width: 110 }}
            inputProps={{ inputMode: 'numeric' }}
          />
          <TextField
            label="Preplanned amount"
            size="small"
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
            sx={{ width: 180 }}
            inputProps={{ inputMode: 'decimal' }}
            helperText="Dollars"
          />
        </Stack>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {/* Dimmed rather than emptied while a request is in flight: the numbers
            are stale for a moment, but blanking or unmounting them makes the
            section jump on every keystroke. */}
        <Box sx={{ opacity: loading ? 0.5 : 1, transition: 'opacity 120ms' }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Plan</TableCell>
                <TableCell align="right">Per payment</TableCell>
                <TableCell align="right">Payments</TableCell>
                <TableCell align="right">Total paid</TableCell>
                <TableCell align="right">Interest paid</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {PLAN_ORDER.map((planType) => {
                const plan = plans?.find((p) => p.planType === planType);
                return (
                  <TableRow key={planType}>
                    <TableCell>{PLAN_LABEL[planType]}</TableCell>
                    {plan?.available ? (
                      <>
                        <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums' }}>
                          {money(plan.perPeriodCents)}
                        </TableCell>
                        <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums' }}>
                          {plan.periods}
                        </TableCell>
                        <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums' }}>
                          {money(plan.totalCents)}
                        </TableCell>
                        {/* What spreading the payments costs over paying up
                            front. Single Pay is the baseline, so it reads $0.00
                            rather than a dash — that zero is the comparison. */}
                        <TableCell
                          align="right"
                          sx={{
                            fontVariantNumeric: 'tabular-nums',
                            color: quotedCents != null && plan.totalCents > quotedCents
                              ? 'warning.main'
                              : 'text.secondary',
                          }}
                        >
                          {quotedCents != null ? money(plan.totalCents - quotedCents) : '—'}
                        </TableCell>
                      </>
                    ) : plan ? (
                      // Not a number, deliberately. A figure here would look
                      // quotable for a plan that cannot be sold at this age.
                      <TableCell colSpan={4} align="right">
                        <Typography variant="body2" color="text.secondary">
                          {plan.reason}
                        </Typography>
                      </TableCell>
                    ) : (
                      // Nothing to show yet — no age typed, or a request in
                      // flight. Blank cells hold the row's height so the
                      // section doesn't jump.
                      <>
                        <TableCell align="right" sx={{ color: 'text.disabled' }}>
                          —
                        </TableCell>
                        <TableCell align="right" sx={{ color: 'text.disabled' }}>
                          —
                        </TableCell>
                        <TableCell align="right" sx={{ color: 'text.disabled' }}>
                          —
                        </TableCell>
                        <TableCell align="right" sx={{ color: 'text.disabled' }}>
                          —
                        </TableCell>
                      </>
                    )}
                  </TableRow>
                );
              })}
              </TableBody>
            </Table>
          </Box>

        <Box sx={{ mt: 2 }}>
          <Typography variant="caption" color="text.secondary">
            Interest paid is what the plan costs above the preplanned amount — Single
            payment is the baseline at $0.00. A plan shown as unavailable is outside the
            rate card for that age; the other terms may still be sellable.
          </Typography>
        </Box>
    </Box>
  );
};

export default PreneedSection;
